import { unzipSync, strFromU8 } from 'fflate';
import { XMLParser, XMLValidator } from 'fast-xml-parser';
import { canonicalPrUrl, validateScope } from './validation.js';
import type { ValidationResult } from './types.js';

type XmlNode = Record<string, unknown>;
const MAX_UPLOAD = 5 * 1024 * 1024;
const MAX_XML = 2 * 1024 * 1024;
const fail = (message: string): ValidationResult => ({ valid: false, errors: [{ path: 'document', message }] });
function children(nodes: unknown, key: string): XmlNode[][] {
  if (!Array.isArray(nodes)) return [];
  const matches: XmlNode[][] = [];
  for (const node of nodes as XmlNode[]) {
    if (Array.isArray(node[key])) matches.push(node[key] as XmlNode[]);
    for (const [name, value] of Object.entries(node)) if (name !== ':@' && Array.isArray(value)) matches.push(...children(value, key));
  }
  return matches;
}
function textOf(nodes: unknown): string {
  if (!Array.isArray(nodes)) return '';
  let text = '';
  for (const node of nodes as XmlNode[]) for (const [key, value] of Object.entries(node)) {
    if (key === '#text') text += String(value);
    else if (key === 'w:tab') text += ' ';
    else if (key !== ':@' && Array.isArray(value)) text += textOf(value) + (key === 'w:p' || key === 'w:br' ? '\n' : '');
  }
  // Word splits a sentence across runs, often with the space on just one side.
  // Preserve those spaces here; trim only after the complete table cell is joined.
  return text;
}
function tableRows(table: XmlNode[]): string[][] {
  return table.filter(node => Array.isArray(node['w:tr'])).map(node => (node['w:tr'] as XmlNode[]).filter(row => Array.isArray(row['w:tc'])).map(cell => textOf(cell['w:tc']).trim()));
}
const splitIds = (value: string) => value.split(/[,;\n]+/).map(s => s.trim()).filter(Boolean);
const optional = (value: string | undefined) => !value || /^none(?: yet)?$/i.test(value.trim()) ? undefined : value;
/** Reads only standard Word tables. It never executes document content or follows external relationships. */
export function parseScopeDocx(bytes: Uint8Array): ValidationResult {
  if (!bytes.length || bytes.length > MAX_UPLOAD) return fail('Upload a DOCX no larger than 5 MiB.');
  try {
    let oversized = false;
    const files = unzipSync(bytes, { filter: file => {
      if (file.name !== 'word/document.xml') return false;
      if (file.originalSize > MAX_XML || file.size > MAX_UPLOAD) { oversized = true; return false; }
      return true;
    } });
    if (oversized) return fail('The document text is too large to import.');
    const source = files['word/document.xml'];
    if (!source || source.length > MAX_XML) return fail('This is not a supported Word DOCX document.');
    const xml = strFromU8(source);
    if (/<!DOCTYPE|<!ENTITY/i.test(xml)) return fail('Document entity declarations are not supported.');
    // Bound nesting before handing untrusted XML to a recursive parser.
    let depth = 0;
    for (const match of xml.matchAll(/<\/?[A-Za-z][^>]*>/g)) {
      const tag = match[0];
      if (tag.startsWith('</')) depth--;
      else if (!tag.endsWith('/>')) depth++;
      if (depth > 80) return fail('Document nesting exceeds the supported template format.');
    }
    if (XMLValidator.validate(xml) !== true) return fail('The Word document contains invalid XML.');
    const tree: unknown = new XMLParser({ preserveOrder: true, ignoreAttributes: true, parseTagValue: false, trimValues: false }).parse(xml);
    const tables = children(tree, 'w:tbl').map(tableRows);
    const metadata = Object.fromEntries((tables.find(t => t.some(row => row[0] === 'Template ID')) ?? []).filter(row => row.length >= 2).map(row => [row[0], row[1]]));
    const find = (first: string) => tables.find(t => t[0]?.[0] === first) ?? [];
    const records = (table: string[][]) => table.slice(1).filter(row => row.some(cell => cell.trim())).map(row => Object.fromEntries((table[0] ?? []).map((key, i) => [key, row[i] ?? ''])));
    const workTables = tables.filter(t => t.some(row => row[0] === 'Work ID'));
    const rawWork = workTables.map(t => Object.fromEntries(t.filter(row => row.length >= 2).map(row => [row[0], row[1]])));
    const badLinks = rawWork.flatMap((row, index) => {
      const value = optional(row['GitHub Links']);
      if (!value) return [];
      const links = value.split(/[\s,;]+/).filter(Boolean);
      return links.some(link => !canonicalPrUrl(link) && !/^https:\/\/github\.com\/[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+\/issues\/[1-9]\d*$/.test(link))
        ? [{ path: `workItems[${index}].requiredPrs`, message: 'GitHub Links must contain complete GitHub PR or issue URLs, separated by spaces or commas, or None yet.' }] : [];
    });
    if (badLinks.length) return { valid: false, errors: badLinks };
    const knownIds = new Set(rawWork.map(row => row['Work ID']));
    const scope = {
      sourceText: textOf(tree).trim(), metadata,
      templateId: metadata['Template ID'], projectReference: metadata['Project Reference'], scopeReference: metadata['Scope Reference'], revision: metadata.Revision,
      projectName: metadata['Project Name'], clientName: metadata['Client Name'],
      phases: records(find('Phase ID')).map(r => ({ id: r['Phase ID'], name: r['Phase Name'], outcome: r['Phase Outcome'] })),
      sections: records(find('Section ID')).map(r => ({ id: r['Section ID'], phaseId: r['Phase ID'], name: r['Section Name'], description: r['Section Description'] })),
      sprints: records(find('Sprint ID')).map(r => ({ id: r['Sprint ID'], phaseId: r['Phase ID'], startDate: r['Start Date'], endDate: r['End Date'], goal: r['Sprint Goal'] })),
      workItems: rawWork.map(r => {
        const links = r['GitHub Links']?.match(/https:\/\/github\.com\/[^\s,;]+/g) ?? [];
        const rule = r['Completion Rule']?.trim().toLowerCase();
        const dependencies = optional(r.Dependencies);
        return {
          id: r['Work ID'], title: r['Work Title'], phaseId: r['Phase ID'], sectionId: r['Section ID'], sprintId: r['Sprint ID'], description: r.Description,
          effortPoints: Number(r['Effort Points']), acceptanceCriteria: (r['Acceptance Criteria'] ?? '').split(/\n+|(?=AC\d+:)/).map(s => s.trim()).filter(Boolean),
          completionRule: rule === 'github merge' ? 'github_merge' : rule === 'recorded acceptance' ? 'recorded_acceptance' : rule,
          acceptanceOwner: optional(r['Acceptance Owner']), owner: optional(r.Owner),
          dependencies: dependencies ? splitIds(dependencies).filter(id => knownIds.has(id) || /^[A-Z]{1,8}[-_]\d+$/.test(id)) : [],
          dependencyNotes: dependencies,
          requiredPrs: links.filter(link => /\/pull\//.test(link)),
          supportingIssueLinks: links.filter(link => /\/issues\//.test(link)),
        };
      }),
      milestones: records(find('Milestone ID')).map(r => ({ id: r['Milestone ID'], name: r['Milestone Name'], workIds: splitIds(r['Work IDs'] ?? ''), targetDate: optional(r['Target Date']) })),
    };
    return validateScope(scope);
  } catch { return fail('Unable to read the standard scope tables. Use an unencrypted Second Sight Scope Input DOCX.'); }
}
export function parseScopeJson(text: string): ValidationResult {
  if (text.length > MAX_XML) return fail('The scope JSON is too large.');
  try { return validateScope(JSON.parse(text.replace(/^\uFEFF/, ''))); } catch { return fail('The scope file is not valid JSON.'); }
}
